/**
 * Printable single-order receipt at /my-orders/:orderId. Reuses the same data
 * as /my-orders but presents it as a full-page receipt with print styles so
 * shoppers can save it as PDF (browser Ctrl/Cmd + P) or print for filing.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft, Printer, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { useShopperAuth } from "@/context/ShopperAuthContext";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

function fmtMoney(amount, currency = "GBP") {
  const symbol = { GBP: "£", USD: "$", EUR: "€" }[currency] || "";
  return `${symbol}${Number(amount || 0).toFixed(2)}`;
}

export default function MyOrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { shopper, loading: authLoading, signIn } = useShopperAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!shopper) { setLoading(false); return; }
    let alive = true;
    axios.get(`${API}/shopper/me/orders`, { withCredentials: true })
      .then(({ data }) => {
        if (!alive) return;
        const match = (data.orders || []).find((o) => o.order_id === orderId);
        if (!match) {
          toast.error("Order not found");
          navigate("/my-orders", { replace: true });
          return;
        }
        setOrder(match);
      })
      .catch(() => { if (alive) toast.error("Failed to load order"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [shopper, authLoading, orderId, navigate]);

  const handlePrint = () => window.print();

  return (
    <PageShell chrome>
      <style>{`
        @media print {
          body { background: #fff !important; }
          [data-testid="pageshell-chrome-top"],
          [data-testid="pageshell-chrome-bottom"],
          .no-print { display: none !important; }
          .receipt-page { padding: 0 !important; }
        }
      `}</style>
      <main className="max-w-3xl mx-auto px-4 py-10 receipt-page">
        <div className="flex items-center justify-between mb-6 no-print">
          <Link
            to="/my-orders"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            data-testid="order-detail-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> Back to my orders
          </Link>
          {order && (
            <Button onClick={handlePrint} variant="outline" size="sm" data-testid="order-detail-print-btn">
              <Printer className="h-4 w-4 mr-1" /> Print / save PDF
            </Button>
          )}
        </div>

        {authLoading || loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !shopper ? (
          <div className="border border-dashed border-border rounded-lg p-8 text-center bg-muted/20" data-testid="order-detail-signin-gate">
            <p className="text-sm text-muted-foreground mb-4">Sign in to view this order.</p>
            <button
              type="button"
              onClick={() => signIn()}
              className="inline-flex items-center gap-2 h-10 px-4 border border-border rounded-md text-sm font-medium bg-background hover:bg-muted/50 transition-colors"
              data-testid="order-detail-signin-btn"
            >
              Sign in with Google
            </button>
          </div>
        ) : order ? (
          <OrderReceipt order={order} />
        ) : null}
      </main>
    </PageShell>
  );
}

function OrderReceipt({ order }) {
  const customer = order.customer || {};
  const address = order.customer?.billing_address || {};
  const currency = order.currency || "GBP";
  const status = order.payment_status || order.status || "pending";
  const badgeCls = status === "paid"
    ? "bg-green-100 text-green-700"
    : status === "failed"
      ? "bg-red-100 text-red-700"
      : "bg-amber-100 text-amber-700";
  return (
    <article
      className="border border-border rounded-lg bg-background p-6 sm:p-10"
      data-testid={`order-receipt-${order.order_id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 pb-6 border-b border-border">
        <div>
          <h1 className="text-2xl font-serif flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" /> Receipt
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Order #{(order.order_id || "").slice(0, 8).toUpperCase()}
          </p>
          <p className="text-xs text-muted-foreground">
            Placed {new Date(order.created_at).toLocaleString()}
          </p>
        </div>
        <span className={`inline-block text-xs px-2 py-1 rounded ${badgeCls} capitalize self-start`}>
          {status}
        </span>
      </div>

      <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 text-sm">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Billed to</div>
          <div className="font-medium">{customer.name || "—"}</div>
          <div className="text-muted-foreground">{customer.email || ""}</div>
          {customer.phone && <div className="text-muted-foreground">{customer.phone}</div>}
        </div>
        {(address.line1 || address.city || address.postcode) && (
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Billing address</div>
            <div>{address.line1}</div>
            {address.line2 && <div>{address.line2}</div>}
            <div>
              {[address.city, address.state, address.postcode].filter(Boolean).join(", ")}
            </div>
            {address.country && <div>{address.country}</div>}
          </div>
        )}
      </section>

      <section className="mt-8">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide border-b border-border">
              <th className="pb-2 font-medium">Item</th>
              <th className="pb-2 text-right font-medium">Qty</th>
              <th className="pb-2 text-right font-medium">Unit</th>
              <th className="pb-2 text-right font-medium">Line total</th>
            </tr>
          </thead>
          <tbody>
            {(order.items || []).map((it, idx) => (
              <tr key={idx} className="border-b border-border">
                <td className="py-3">
                  <div className="flex items-center gap-3">
                    {it.image_url ? (
                      <img src={it.image_url} alt="" className="h-10 w-10 rounded object-cover border" />
                    ) : (
                      <div className="h-10 w-10 rounded bg-muted/40 border" />
                    )}
                    <span className="font-medium">{it.name}</span>
                  </div>
                </td>
                <td className="py-3 text-right whitespace-nowrap">{it.quantity}</td>
                <td className="py-3 text-right whitespace-nowrap">{fmtMoney(it.price, currency)}</td>
                <td className="py-3 text-right whitespace-nowrap font-medium">
                  {fmtMoney(Number(it.price || 0) * Number(it.quantity || 1), currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mt-6 flex justify-end">
        <div className="w-full sm:w-64 text-sm space-y-2">
          <div className="flex justify-between text-muted-foreground">
            <span>Subtotal</span>
            <span>{fmtMoney(order.subtotal, currency)}</span>
          </div>
          {Number(order.discount_amount) > 0 && (
            <div className="flex justify-between text-green-700">
              <span>Discount{order.discount_code ? ` (${order.discount_code})` : ""}</span>
              <span>-{fmtMoney(order.discount_amount, currency)}</span>
            </div>
          )}
          <div className="flex justify-between border-t border-border pt-2 font-semibold text-base">
            <span>Total paid</span>
            <span>{fmtMoney(order.total, currency)}</span>
          </div>
        </div>
      </section>

      <footer className="mt-10 pt-4 border-t border-border text-xs text-muted-foreground text-center">
        Thanks for shopping with us. Questions about this order? Just reply to your receipt email.
      </footer>
    </article>
  );
}
